import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Eye, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import {
  MASS_CASES,
  MASS_DIRECTIONS,
  MASS_DIRECTION_LABELS,
  checkMassPrediction,
  evaluateMassModel,
  formatGrams,
  type MassCase,
  type MassDirection,
  type MassModelResult,
} from './conservationOfMass';
import { ConservationExplore } from './ConservationOfMassExplore';
import { BalanceScene, FeedbackPanel, MassLedger } from './ConservationOfMassVisuals';

function formatEquation(equation: string) {
  return equation
    .replace('->', '→')
    .replace(/([A-Za-z)])(\d+)/g, (_, letter: string, digits: string) =>
      `${letter}${digits.replace(/\d/g, (digit) => '₀₁₂₃₄₅₆₇₈₉'[Number(digit)])}`,
    );
}

function changeSentence(model: MassModelResult) {
  if (model.direction === 'same') return 'showed no measurable change';
  return `${model.direction === 'increase' ? 'gained' : 'lost'} ${formatGrams(Math.abs(model.changeG))}`;
}

/** Mass readings: Explore (own inputs, working always visible) or Practice (predict first, then run). */
export function MassReadingsMode() {
  const [view, setView] = useState<'explore' | 'practice'>('explore');
  return (
    <div>
      <div className="mb-4 grid max-w-xs grid-cols-2 rounded-full bg-surface p-1" role="group" aria-label="Mass readings view">
        {(['explore', 'practice'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={view === option}
            onClick={() => setView(option)}
            className={`rounded-full px-4 py-2 text-sm font-extrabold ${view === option ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {option === 'explore' ? 'Explore' : 'Practice'}
          </button>
        ))}
      </div>
      {view === 'explore' ? <ConservationExplore /> : <MassPractice />}
    </div>
  );
}

function MassPractice() {
  const [entry, setEntry] = useState<MassCase>(MASS_CASES[0]);
  const [prediction, setPrediction] = useState<MassDirection | null>(null);
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('MassReadingsMode', { entry: tutorSelection(entry), prediction, revealed });
  const outcome = evaluateMassModel(entry);
  const model = 'model' in outcome ? outcome.model : null;
  const result = revealed && prediction ? checkMassPrediction(entry, prediction) : null;

  const choose = (next: MassCase) => {
    setEntry(next);
    setPrediction(null);
    setRevealed(false);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Choose an experiment</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1" aria-label="Mass experiments">
          {MASS_CASES.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === entry.id}
              onClick={() => choose(candidate)}
              className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                candidate.id === entry.id
                  ? 'border-brand-sky bg-wash-sky text-ink'
                  : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
              }`}
            >
              {candidate.title}
              <span className="mt-0.5 block text-xs font-bold text-slate2">
                {candidate.system === 'open' ? 'Open system' : 'Closed system'}
              </span>
            </button>
          ))}
        </div>
      </aside>

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <h3 className="text-xl font-extrabold text-ink">{entry.title}</h3>
        <p className="mt-2 font-medium leading-7 text-slate2">{entry.setup}</p>
        <p className="mt-2 break-words font-mono text-sm font-bold text-ink">{formatEquation(entry.equation)}</p>

        {model ? (
          <>
            <div className="mt-4 rounded-2xl border border-hairline bg-surface p-4">
              <BalanceScene entry={entry} model={model} revealed={revealed} />
            </div>

            <fieldset className="mt-5">
              <legend className="text-sm font-extrabold text-ink">
                Predict: what will the balance show after the reaction?
              </legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                {MASS_DIRECTIONS.map((direction) => (
                  <button
                    key={direction}
                    type="button"
                    disabled={revealed}
                    aria-pressed={prediction === direction}
                    onClick={() => setPrediction(direction)}
                    className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold disabled:cursor-default ${
                      prediction === direction ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-ink'
                    }`}
                  >
                    {MASS_DIRECTION_LABELS[direction]}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={!prediction || revealed}
                onClick={() => setRevealed(true)}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Eye className="h-4 w-4" aria-hidden="true" /> Run the reaction
              </button>
              <button
                type="button"
                onClick={() => choose(entry)}
                className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
              </button>
            </div>
          </>
        ) : (
          <p role="alert" className="mt-4 rounded-2xl bg-wash-coral p-4 text-sm font-bold text-ink">
            {'reason' in outcome ? outcome.reason : null} No reading is shown.
          </p>
        )}

        {result ? (
          <FeedbackPanel
            correct={result.correct}
            title={
              result.correct
                ? `Correct — ${MASS_DIRECTION_LABELS[result.expected]}`
                : `Not quite — ${MASS_DIRECTION_LABELS[result.expected].toLowerCase()}`
            }
          >
            <p>{result.feedback}</p>
            <p className="text-ink">
              <strong>
                {formatGrams(result.model.beforeG)} → {formatGrams(result.model.afterG)}
              </strong>{' '}
              (the balance {changeSentence(result.model)}).
            </p>
            {result.model.crossings.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {result.model.crossings.map((crossing) => (
                  <li key={`${crossing.gas.formula}-${crossing.gas.direction}`}>
                    {crossing.gas.name} {crossing.gas.direction === 'in' ? 'entered from the air' : 'left to the air'}:{' '}
                    {formatGrams(crossing.massG)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm">No gas crossed the boundary of the system.</p>
            )}
            <p className="text-sm">
              Atoms are rearranged, never created or destroyed: the substances that react weigh the
              same as the substances that form.
            </p>
            <MassLedger model={result.model} />
            <p className="text-xs font-bold leading-5">Model note: {entry.boundary}</p>
          </FeedbackPanel>
        ) : null}
      </section>
    </div>
  );
}
